import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import MuiButton from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import React, { FC, MouseEvent, useCallback, useEffect, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import DropZone from "../../../components/elements/DropZone";
import api from "../../../api";
import { errorToaster, successToaster } from "../../../helpers/toasters";
import { useGlobalSelectedAdvertiser } from "../../../hooks/useGlobalSelectedAdvertiser";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ZipCodeSet = {
  id: string;
  name: string;
  zipCodes: string[];
  updatedAt: string;
};

type ModalState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; set: ZipCodeSet }
  | { mode: "delete"; set: ZipCodeSet };

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const PageRoot = styled("div")({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 20,
  padding: "0 0 32px",
});

const Header = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
});

const CreateButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  fontSize: 13,
  fontWeight: 500,
  padding: "6px 14px",
  borderRadius: 6,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { background: theme.palette.brand.coralHover, boxShadow: "0 2px 6px rgba(0,0,0,0.12)" },
}));

const Card = styled("div")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  overflow: "hidden",
}));

const TableEl = styled("table")({ width: "100%", borderCollapse: "collapse" });

const Thead = styled("thead")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.gray50,
}));

const Th = styled("th")(({ theme }: { theme: Theme }) => ({
  padding: "10px 16px",
  textAlign: "left" as const,
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  color: theme.palette.brand.gray500,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
}));

const ClickableRow = styled("tr")(({ theme }: { theme: Theme }) => ({
  height: 52,
  borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  cursor: "pointer",
  "&:hover": { background: theme.palette.brand.gray50 },
}));

const ExpandRow = styled("tr")(({ theme }: { theme: Theme }) => ({
  "& td": {
    padding: "0 !important",
    borderBottom: `1px solid ${theme.palette.brand.gray200}`,
  },
}));

const Td = styled("td")(({ theme }: { theme: Theme }) => ({
  padding: "0 16px",
  fontSize: 13,
  color: theme.palette.brand.gray900,
  verticalAlign: "middle" as const,
}));

const CountCell = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
});

const ExpandIconButton = styled(IconButton)(({ theme }: { theme: Theme }) => ({
  padding: 2,
  color: theme.palette.brand.gray500,
}));

const TagsWrapper = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
  padding: "10px 16px 12px",
});

const ZipTag = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 400,
  color: theme.palette.brand.gray700,
  background: theme.palette.brand.gray100,
  border: `1px solid ${theme.palette.brand.gray200}`,
}));

const ShowMoreButton = styled("button")(({ theme }: { theme: Theme }) => ({
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 500,
  color: theme.palette.brand.blueAccent,
  background: "none",
  border: `1px solid ${theme.palette.brand.blueAccent}`,
  cursor: "pointer",
  "&:hover": { opacity: 0.8 },
}));

const MoreButton = styled("button")(({ theme }: { theme: Theme }) => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  color: theme.palette.brand.gray400,
  padding: 4,
  borderRadius: 6,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    background: theme.palette.brand.gray100,
    color: theme.palette.brand.gray700,
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.brand.gray700,
  minWidth: 200,
  "&:hover": { background: theme.palette.brand.gray50 },
}));

const DangerMenuItem = styled(StyledMenuItem)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.errorRed,
}));

const MoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="1" />
    <circle cx="19" cy="12" r="1" />
    <circle cx="5" cy="12" r="1" />
  </svg>
);

const EmptyState = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "48px 24px",
  textAlign: "center" as const,
  color: theme.palette.brand.gray500,
  fontSize: 14,
}));

const LoadingState = styled("div")({
  padding: "48px 24px",
  display: "flex",
  justifyContent: "center",
});

const AdvertiserPrompt = styled(Typography)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray500,
  fontSize: 14,
  padding: "32px 0",
}));

const ModalBody = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  minWidth: 480,
  paddingTop: 8,
});

const SaveButton = styled(MuiButton)(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.coral,
  color: theme.palette.brand.white,
  fontWeight: 500,
  textTransform: "none",
  borderRadius: 6,
  boxShadow: "none",
  "&:hover": { background: theme.palette.brand.coralHover, boxShadow: "none" },
}));

const ZipPreview = styled("div")(({ theme }: { theme: Theme }) => ({
  fontSize: 12,
  color: theme.palette.brand.gray500,
  marginTop: -8,
}));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseZipText(raw: string): string[] {
  const result: string[] = [];
  for (const row of raw.split("\n")) {
    for (const part of row.split(",")) {
      const z = part.trim();
      if (z) result.push(z);
    }
  }
  return [...new Set(result)];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type MenuAnchor = { el: HTMLElement; set: ZipCodeSet } | null;

const ZipCodesView: FC = () => {
  const { selectedAdvertiser } = useGlobalSelectedAdvertiser();
  const brandgenAdvertiserId = selectedAdvertiser?.brandgenAdvertiserId ?? null;

  const [sets, setSets] = useState<ZipCodeSet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState<ModalState>({ mode: "closed" });
  const [isSaving, setIsSaving] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<MenuAnchor>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [showAllIds, setShowAllIds] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string, e: MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // form state shared between create and edit
  const [formName, setFormName] = useState("");
  const [formZipText, setFormZipText] = useState("");
  const [formZipCodes, setFormZipCodes] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState(false);

  const fetchSets = useCallback(async () => {
    if (!brandgenAdvertiserId) {
      setSets([]);
      return;
    }
    setIsLoading(true);
    try {
      const { payload } = await api.getZipCodeSets(brandgenAdvertiserId);
      setSets(Array.isArray(payload) ? payload : []);
    } catch {
      setSets([]);
    } finally {
      setIsLoading(false);
    }
  }, [brandgenAdvertiserId]);

  useEffect(() => {
    fetchSets();
  }, [fetchSets]);

  const openCreate = () => {
    setFormName("");
    setFormZipText("");
    setFormZipCodes([]);
    setUploadError(false);
    setModal({ mode: "create" });
  };

  const openEdit = (set: ZipCodeSet) => {
    setFormName(set.name);
    setFormZipText(set.zipCodes.join("\n"));
    setFormZipCodes(set.zipCodes);
    setUploadError(false);
    setModal({ mode: "edit", set });
  };

  const closeModal = () => setModal({ mode: "closed" });

  const handleDrop = useCallback((acceptedFiles: File[]) => {
    setUploadError(false);
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.readAsText(file);
      reader.addEventListener("load", (event) => {
        try {
          const csv = event.target!.result as string;
          const parsed = parseZipText(csv);
          setFormZipCodes(parsed);
          setFormZipText(parsed.join("\n"));
        } catch {
          setUploadError(true);
        }
      });
      reader.addEventListener("error", () => setUploadError(true));
    });
  }, []);

  const handleTextChange = (value: string) => {
    setFormZipText(value);
    setFormZipCodes(parseZipText(value));
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      errorToaster("Please enter a name for this set.", 4000);
      return;
    }
    if (formZipCodes.length === 0) {
      errorToaster("Please add at least one zip code.", 4000);
      return;
    }
    if (!brandgenAdvertiserId) return;
    setIsSaving(true);
    try {
      if (modal.mode === "create") {
        await api.createZipCodeSet({ name: formName.trim(), brandgenAdvertiserId, zipCodes: formZipCodes });
        successToaster("Zip code set created.");
      } else if (modal.mode === "edit") {
        await api.updateZipCodeSet(modal.set.id, { name: formName.trim(), zipCodes: formZipCodes });
        successToaster("Zip code set updated.");
      }
      await fetchSets();
      closeModal();
    } catch {
      errorToaster("Failed to save. Please try again.", 4000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (modal.mode !== "delete") return;
    setIsSaving(true);
    try {
      await api.deleteZipCodeSet(modal.set.id);
      successToaster("Zip code set deleted.");
      await fetchSets();
      closeModal();
    } catch {
      errorToaster("Failed to delete. Please try again.", 4000);
    } finally {
      setIsSaving(false);
    }
  };

  const isFormOpen = modal.mode === "create" || modal.mode === "edit";

  return (
    <PageRoot>
      <Header>{brandgenAdvertiserId && <CreateButton onClick={openCreate}>+ Create New Set</CreateButton>}</Header>

      {!brandgenAdvertiserId ? (
        <AdvertiserPrompt>Please select an advertiser to manage zip code sets.</AdvertiserPrompt>
      ) : isLoading ? (
        <LoadingState>
          <CircularProgress size={32} />
        </LoadingState>
      ) : sets.length === 0 ? (
        <Card>
          <EmptyState>No zip code sets yet. Create one to get started.</EmptyState>
        </Card>
      ) : (
        <Card>
          <TableEl>
            <Thead>
              <tr>
                <Th>Name</Th>
                <Th>Zip Codes</Th>
                <Th>Last Modified</Th>
                <Th style={{ width: 120 }} />
              </tr>
            </Thead>
            <tbody>
              {sets.map((set) => {
                const isExpanded = expandedIds.has(set.id);
                const showAll = showAllIds.has(set.id);
                const displayed = showAll ? set.zipCodes : set.zipCodes.slice(0, 100);
                const remaining = set.zipCodes.length - 100;
                return (
                  <React.Fragment key={set.id}>
                    <ClickableRow
                      onClick={() =>
                        setExpandedIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(set.id)) next.delete(set.id);
                          else next.add(set.id);
                          return next;
                        })
                      }
                    >
                      <Td>{set.name}</Td>
                      <Td>
                        <CountCell>
                          <ExpandIconButton
                            size="small"
                            aria-label={isExpanded ? "collapse" : "expand"}
                            onClick={(e: MouseEvent) => toggleExpand(set.id, e)}
                          >
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </ExpandIconButton>
                          {set.zipCodes.length.toLocaleString()}
                        </CountCell>
                      </Td>
                      <Td>{new Date(set.updatedAt).toLocaleDateString()}</Td>
                      <Td>
                        <MoreButton
                          type="button"
                          aria-label={`Actions for ${set.name}`}
                          onClick={(e: MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            setMenuAnchor({ el: e.currentTarget, set });
                          }}
                        >
                          <MoreIcon />
                        </MoreButton>
                      </Td>
                    </ClickableRow>
                    <ExpandRow>
                      <td colSpan={4}>
                        <Collapse in={isExpanded} unmountOnExit>
                          <TagsWrapper>
                            {displayed.map((zip) => (
                              <ZipTag key={zip}>{zip}</ZipTag>
                            ))}
                            {!showAll && remaining > 0 && (
                              <ShowMoreButton
                                type="button"
                                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                                  e.stopPropagation();
                                  setShowAllIds((prev) => new Set([...prev, set.id]));
                                }}
                              >
                                +{remaining.toLocaleString()} more
                              </ShowMoreButton>
                            )}
                          </TagsWrapper>
                        </Collapse>
                      </td>
                    </ExpandRow>
                  </React.Fragment>
                );
              })}
            </tbody>
          </TableEl>
        </Card>
      )}

      {/* Row actions menu */}
      <Menu
        anchorEl={menuAnchor?.el ?? null}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <StyledMenuItem
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            if (menuAnchor) {
              openEdit(menuAnchor.set);
            }
            setMenuAnchor(null);
          }}
        >
          Edit
        </StyledMenuItem>
        <DangerMenuItem
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            if (menuAnchor) {
              setModal({ mode: "delete", set: menuAnchor.set });
            }
            setMenuAnchor(null);
          }}
        >
          Delete
        </DangerMenuItem>
      </Menu>

      {/* Create / Edit Modal */}
      <Dialog open={isFormOpen} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle>{modal.mode === "create" ? "Create Zip Code Set" : "Edit Zip Code Set"}</DialogTitle>
        <DialogContent>
          <ModalBody>
            <TextField
              label="Set Name"
              variant="outlined"
              size="small"
              fullWidth
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
            />
            <DropZone
              explanatoryText="Upload a CSV file (one zip code per line or comma-separated)."
              sampleLink="https://www.brandgen.io/hubfs/Help%20Documents/BG_Sample_ZipCodes.csv"
              onDrop={handleDrop}
              didErrorOccurInUpload={uploadError}
            />
            <TextField
              label="Or paste zip codes (one per line or comma-separated)"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              rows={5}
              value={formZipText}
              onChange={(e) => handleTextChange(e.target.value)}
            />
            {formZipCodes.length > 0 && (
              <ZipPreview>
                {formZipCodes.length.toLocaleString()} zip code{formZipCodes.length !== 1 ? "s" : ""} loaded
              </ZipPreview>
            )}
          </ModalBody>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <MuiButton onClick={closeModal} disabled={isSaving}>
            Cancel
          </MuiButton>
          <SaveButton onClick={handleSave} disabled={isSaving} variant="contained">
            {isSaving ? "Saving…" : "Save"}
          </SaveButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={modal.mode === "delete"} onClose={closeModal} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Zip Code Set</DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: 14 }}>
            Are you sure you want to delete <strong>{modal.mode === "delete" ? modal.set.name : ""}</strong>? This
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          <MuiButton onClick={closeModal} disabled={isSaving}>
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleDelete}
            disabled={isSaving}
            color="secondary"
            variant="contained"
            style={{ textTransform: "none" }}
          >
            {isSaving ? "Deleting…" : "Delete"}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </PageRoot>
  );
};

export default ZipCodesView;
